import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Container,
  Card,
  CardContent,
  CardMedia,
  Button,
  CardActionArea,
  Pagination,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import buybanner from "@assets/images/3-Real-Estate-Banner.jpg";
import ContactSection from "../Home/contactSection";
import { motion } from "framer-motion";
import { FaInfoCircle, FaTimes } from "react-icons/fa";
import axios from "axios";
import contactBg from "@assets/images/contactbg.jpg";
import { useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import { useFilter } from "@context/filterContext.jsx";
import SEO from "../../../utils/SeoComponent";
import { ROUTES } from "../../../constants/routes";

// Styled Components
const PageSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(10, 0),
  position: "relative",
  overflow: "hidden",
}));

const HeroSection = styled(PageSection)(({ theme }) => ({
  backgroundColor: "#f8f9fa",
  backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url(${buybanner})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  color: "white",
  padding: theme.spacing(15, 0),
  textAlign: "center",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "150px",
    background: "linear-gradient(to top, #fff, transparent)",
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  position: "relative",
  fontWeight: 700,
  marginBottom: theme.spacing(6),
  paddingBottom: theme.spacing(2),
  display: "inline-block",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "80px",
    height: "4px",
    backgroundColor: "#C10E14",
  },
}));

const DeveloperCard = styled(Card)(({ theme }) => ({
  height: "100%",
  borderRadius: "12px",
  overflow: "hidden",
  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
  transition: "transform 0.3s ease, box-shadow 0.3s ease",
  "&:hover": {
    transform: "translateY(-10px)",
    boxShadow: "0 16px 32px rgba(0, 0, 0, 0.15)",
  },
}));

const DeveloperCardMedia = styled(CardMedia)(({ theme }) => ({
  width: 240,
  height: 200,
  objectFit: "contain",
  backgroundColor: "#f8f9fc",
  padding: theme.spacing(1),
  margin: "auto",
}));

const PaginationContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  marginTop: theme.spacing(6),
  marginBottom: theme.spacing(10),
}));

const ClearFilterContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  marginBottom: theme.spacing(4),
  marginTop: theme.spacing(2),
}));

const ClearFilterButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#A30B11d",
  color: "#fff",
  padding: theme.spacing(1, 3),
  borderRadius: "25px",
  fontWeight: 600,
  textTransform: "none",
  boxShadow: "0 4px 12px rgba(244, 67, 54, 0.3)",
  "&:hover": {
    backgroundColor: "#d32f2f",
    boxShadow: "0 6px 16px rgba(244, 67, 54, 0.4)",
  },
  "& .MuiButton-startIcon": {
    marginRight: theme.spacing(1),
  },
}));

const SearchResultsBadge = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  backgroundColor: "#e3f2fd",
  color: "#1976d2",
  padding: theme.spacing(0.5, 2),
  borderRadius: "20px",
  fontSize: "0.875rem",
  fontWeight: 600,
  marginBottom: theme.spacing(2),
  border: "1px solid #bbdefb",
}));

const Developer = () => {
  const [developers, setDevelopers] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;
  const [totalPages, setTotalPages] = useState(1);
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get("query");
  const [searchTerm, setSearchTerm] = useState(searchQuery);
  const DESCO_API_BASE = import.meta.env.VITE_DESCO_API_BASE;

  const {
    filteredUsers,
    setFilteredUsers,
    isFilterApplied,
    setIsFilterApplied,
  } = useFilter();

  // Sample data in case API fails
  const sampleDevelopers = [
    { title: "Emaar Properties", image: "https://via.placeholder.com/180x180", slug: "emaar-properties" },
    { title: "DAMAC Properties", image: "https://via.placeholder.com/180x180", slug: "damac-properties" },
    { title: "Nakheel", image: "https://via.placeholder.com/180x180", slug: "nakheel" },
    { title: "Dubai Properties", image: "https://via.placeholder.com/180x180", slug: "dubai-properties" },
    { title: "Sobha Realty", image: "https://via.placeholder.com/180x180", slug: "sobha-realty" },
    { title: "Meraas", image: "https://via.placeholder.com/180x180", slug: "meraas" },
    { title: "Omniyat", image: "https://via.placeholder.com/180x180", slug: "omniyat" },
    {
      title: "Azizi Developments",
      image: "https://via.placeholder.com/180x180",
      slug: "azizi-developments"
    },
    { title: "MAG Developments", image: "https://via.placeholder.com/180x180", slug: "mag-developments" },
    {
      title: "Binghatti Developers",
      image: "https://via.placeholder.com/180x180",
      slug: "binghatti-developers"
    },
    {
      title: "Danube Properties",
      image: "https://via.placeholder.com/180x180",
      slug: "danube-properties"
    },
    { title: "Deyaar", image: "https://via.placeholder.com/180x180", slug: "deyaar" },
  ];

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${DESCO_API_BASE}/developers/index`)
      .then((response) => {
        if (response.data && response.data.data) {
          const fetchedDevelopers = response.data.data;
          setDevelopers(fetchedDevelopers);
          setTotalPages(Math.ceil(fetchedDevelopers.length / itemsPerPage));
        } else {
          console.log(
            "Fallback to sample data if API response format is unexpected "
          );
          setDevelopers(sampleDevelopers);
          setTotalPages(Math.ceil(sampleDevelopers.length / itemsPerPage));
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching developers:", error);
        setDevelopers(sampleDevelopers);
        setTotalPages(Math.ceil(sampleDevelopers.length / itemsPerPage));
        setLoading(false);
      });
  }, []);

  // Navigation helper function
  const handleDeveloperClick = (developer) => {
    const developerSlug = developer?.slug || developer?.title?.toLowerCase().replace(/\s+/g, '-');
    if (developerSlug) {
      navigate(ROUTES.DEVELOPER_DETAIL(developerSlug));
    } else {
      console.error('No slug or title found for developer:', developer);
    }
  };

  // Get current developers based on pagination
  const getCurrentDevelopers = (filteredData) => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    if (filteredData) {
      return filteredData.slice(indexOfFirstItem, indexOfLastItem);
    }
    return developers.slice(indexOfFirstItem, indexOfLastItem);
  };

  // Handle page change
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    const element = document.getElementById("developers-list");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Clear filter function
  const handleClearFilter = () => {
    setSearchTerm("");
    setFilteredUsers([]);
    setIsFilterApplied(false);
    setCurrentPage(1);
    
    // Update total pages back to original developers count
    setTotalPages(Math.ceil(developers.length / itemsPerPage));
    
    // Scroll to developers list
    const element = document.getElementById("developers-list");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleSearch = async () => {
    if (!searchTerm?.trim()) {
      // If no search term, reset to show all developers
      setFilteredUsers([]);
      setIsFilterApplied(false);
      setTotalPages(Math.ceil(developers.length / itemsPerPage));
      return;
    }

    try {
      const formData = new FormData();
      formData.append("title", searchTerm.trim());

      const response = await axios.post(
        `${DESCO_API_BASE}/developers/filter`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data && response.data.data) {
        const filteredData = response.data.data;
        setFilteredUsers(filteredData);
        setIsFilterApplied(true);
        setCurrentPage(1);
        setTotalPages(Math.ceil(filteredData.length / itemsPerPage));
      }
    } catch (error) {
      console.error("Error fetching filtered data:", error);
      // On error, reset filter state
      setFilteredUsers([]);
      setIsFilterApplied(false);
    }
  };

  useEffect(() => {
    if (searchQuery) {
      setSearchTerm(searchQuery);
    }
  }, [searchQuery]);

  useEffect(() => {
    const timer = setTimeout(() => {
      handleSearch();
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Update total pages when filter state changes
  useEffect(() => {
    if (isFilterApplied && filteredUsers) {
      setTotalPages(Math.ceil(filteredUsers.length / itemsPerPage));
    } else {
      setTotalPages(Math.ceil(developers.length / itemsPerPage));
    }
  }, [isFilterApplied, filteredUsers, developers, itemsPerPage]);

  // Determine which data to display
  const displayData = isFilterApplied && filteredUsers ? filteredUsers : developers;
  const currentDisplayData = getCurrentDevelopers(isFilterApplied ? filteredUsers : null);

  return (
    <>
      <SEO slug={"developers"} />
      <Box className="developers-page">
        {/* Hero Section */}
        <HeroSection className="hero-section">
          <Container
            maxWidth="md"
            sx={{
              mt: { xs: 6, md: 14 },
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Typography
                variant="h2"
                component="h1"
                gutterBottom
                fontWeight={700}
              >
                Real Estate Developers in Dubai
              </Typography>
              <Typography
                variant="h5"
                component="p"
                sx={{ mb: 4, opacity: 0.9 }}
              >
                Partnering with Dubai's premier developers to bring you
                exceptional properties
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => {
                  const element = document.getElementById("developers-list");
                  if (element) {
                    element.scrollIntoView({ behavior: "smooth" });
                  }
                }}
                sx={{
                  borderRadius: "50px",
                  py: 1.5,
                  px: 4,
                  fontSize: "1rem",
                  boxShadow: "0 4px 14px rgba(0, 0, 0, 0.25)",
                  backgroundColor: "#C10E14",
                  "&:hover": {
                    backgroundColor: "#A30B11",
                  },
                }}
              >
                View Developers
              </Button>
            </motion.div>
          </Container>
        </HeroSection>

        {/* Developers List Section */}
        <PageSection id="developers-list" className="developers-list-section">
          <Container>
            <Box sx={{ textAlign: "center", mb: 6 }}>
              <Typography
                variant="h4"
                component="h2"
                fontWeight={700}
                sx={{ mb: 2 }}
              >
                {isFilterApplied
                  ? "Search Results"
                  : "Our Partner Developers"}
              </Typography>
              
              {/* Search Results Badge */}
              {isFilterApplied && (
                <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                  <SearchResultsBadge>
                    Found {displayData?.length || 0} developer{displayData?.length !== 1 ? 's' : ''} 
                    {searchTerm && ` for "${searchTerm}"`}
                  </SearchResultsBadge>
                </Box>
              )}

              <Typography
                variant="subtitle1"
                color="text.secondary"
                sx={{ maxWidth: "700px", mx: "auto" }}
              >
                {isFilterApplied
                  ? "Developers matching your search criteria"
                  : "Discover the visionary developers shaping Dubai's iconic skyline and communities"}
              </Typography>
            </Box>

            {/* Clear Filter Button - Only show when filter is applied */}
            {isFilterApplied && (
              <ClearFilterContainer>
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <ClearFilterButton
                    startIcon={<FaTimes />}
                    onClick={handleClearFilter}
                  >
                    Clear Search Filter
                  </ClearFilterButton>
                </motion.div>
              </ClearFilterContainer>
            )}

            {loading ? (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <Typography variant="h6">Loading developers...</Typography>
              </Box>
            ) : currentDisplayData?.length > 0 ? (
              <Grid container spacing={4}>
                {currentDisplayData.map((developer, index) => (
                  <Grid item xs={12} sm={6} md={3} key={developer?.id || index}>
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                    >
                      <DeveloperCard onClick={() => handleDeveloperClick(developer)}>
                        <CardActionArea>
                          <DeveloperCardMedia
                            component="img"
                            image={developer.image || "https://via.placeholder.com/180x180"}
                            title={developer.title}
                            alt={developer.title}
                          />
                          <CardContent sx={{ textAlign: "center" }}>
                            <Typography
                              variant="h6"
                              component="h3"
                              fontWeight={600}
                            >
                              {developer.title}
                            </Typography>
                            <Button
                              size="small"
                              sx={{ mt: 1, color: "#C10E14" }}
                              endIcon={<FaInfoCircle />}
                            >
                              View Projects
                            </Button>
                          </CardContent>
                        </CardActionArea>
                      </DeveloperCard>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {isFilterApplied 
                    ? "No developers found matching your search criteria."
                    : "No developers available at the moment."
                  }
                </Typography>
                {isFilterApplied && (
                  <Button
                    variant="outlined"
                    onClick={handleClearFilter}
                    sx={{
                      borderColor: "#C10E14",
                      color: "#C10E14",
                      "&:hover": {
                        borderColor: "#A30B11",
                        backgroundColor: "rgba(193, 14, 20, 0.04)",
                      },
                    }}
                  >
                    View All Developers
                  </Button>
                )}
              </Box>
            )}
          </Container>
        </PageSection>

        {/* Pagination */}
        {totalPages > 1 && (
          <PaginationContainer>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
                sx={{
                  "& .MuiPaginationItem-root": {
                    color: "#666",
                    fontWeight: 600,
                  },
                  "& .Mui-selected": {
                    backgroundColor: "#C10E14 !important",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "#A30B11 !important",
                    },
                  },
                }}
              />
            </motion.div>
          </PaginationContainer>
        )}

        {/* Contact Section */}
        <Box
          style={{
            padding: " 80px 0px ",
            backgroundImage: `url(${contactBg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        >
          <ContactSection />
        </Box>
      </Box>
    </>
  );
};

export default Developer;